import { emailDomainRegex, s3URIRegex } from "pages/mlops/ducks/utils";
import * as yup from "yup";

export const userOnboardingValidations = yup
.object({
    pod: yup.string().required().label("Pod name"),
    name: yup.string().required().label("Name"),
    email: yup.string().required("Email is required").email().matches(emailDomainRegex, "Invalid email domain. Please enter a toyota email id."),
    role: yup.string().required().label("Role"),
    services: yup.array().label("Services"),
    description: yup.string().trim().required().max(400).label("Description"),
    businessJustification: yup.string().trim().required().max(400).label("Business justification"),
    comment: yup.string(),
});

const templateCreationGeneralValidations = {
    pod_name: yup.string().required().matches(/^[A-Za-z_-]+$/, "Invalid Pod name").max(150).label("Pod name"),
    author: yup.string().email().required().matches(emailDomainRegex, "Invalid email domain. Please enter a toyota email id.").max(320).label("Author email"),
    team_name: yup.string().required().matches(/^[A-Za-z_-]+$/, "Invalid Team name").max(150).label("Team name"),
    team_email_address: yup.string().email().required().matches(emailDomainRegex, "Invalid email domain. Please enter a toyota email id.").max(320).label("Team name"),
    project_name: yup.string().required().matches(/^[A-Za-z_-]+$/, "Invalid Project name").max(150).label("Project name"),
};

const modelConfigurationValidation = {
    model_mode: yup.string().required("Model mode is required."),
    modelConfigurations: yup.array()
    .of(
        yup.object().shape({
            wandb_project_name: yup.string().required().max(150).label("Wandb project name"),
            model_name_custom: yup.string().required().max(150).label("Model name"),
            wandb_model_version: yup.string().required().max(50).label("Wandb model version"),
            // wandb_alias: yup.string().when("wandb_model_version", {
            //     is: (wandb_model_version: string) => !wandb_model_version,
            //     then: () => yup.string().required("Wandb alias is required.").max(50, "Wandb alias must be at most 50 characters")
            // }),
        }, [
            ["wandb_model_version", "wandb_alias"],
        ])
    ),
};

const githubConfigValidation = {
    github_user_id: yup.string().when("template_creation_option", (template_creation_option, schema) =>
        String(template_creation_option) === "pushToGithub" ? yup.string().required().max(40).label("Github username") : schema
    ),
    pat_token:  yup.string().when("template_creation_option", (template_creation_option, schema) =>
        String(template_creation_option) === "pushToGithub" ? yup.string().required().max(40).label("Github token") : schema
    ),
    template_creation_option: yup.string().required().matches(/^[A-Za-z_-]+$/, "Pod name should contain only alphabets,- & _.").max(150).label("Pod name"),
};

const schedulingValidation = {
    enable_schedule: yup.string().max(50).label("Enable schedule"),
    minute: yup.string().typeError("Invalid value").when("enable_schedule", (enable_schedule, schema) =>
        String(enable_schedule) === "true" ? yup.string().required().max(5) : schema
    ).label("Minute"),
    hour: yup.string().typeError("Invalid value").when("enable_schedule", (enable_schedule, schema) =>
        String(enable_schedule) === "true" ? yup.string().required().max(5) : schema
    ).label("Hour"),
    dayOfMonth: yup.string().typeError("Invalid value").when("enable_schedule", (enable_schedule, schema) =>
        String(enable_schedule) === "true" ? yup.string().required().max(5) : schema
    ).label("Day of month"),
    month: yup.string().typeError("Invalid value").when("enable_schedule", (enable_schedule, schema) =>
        String(enable_schedule) === "true" ? yup.string().required().max(5) : schema
    ).label("Month"),
    dayOfWeek: yup.string().typeError("Invalid value").min(0).max(7).when("enable_schedule", (enable_schedule, schema) =>
        String(enable_schedule) === "true" ? yup.string().required().max(5) : schema
    ).label("Day of week"),
    year: yup.string().typeError("Invalid value").when("enable_schedule", (enable_schedule, schema) =>
        String(enable_schedule) === "true" ? yup.number().typeError("Year must be a number").required().min(new Date().getFullYear()).max(2099) : schema
    ).label("Year"),
}

export const batchInferencingValidations = yup
.object({
    ...templateCreationGeneralValidations,
    ...modelConfigurationValidation,
    ...githubConfigValidation,
    ...schedulingValidation,

    // Transform job
    batch_strategy: yup.string().required().max(150).label("Batch strategy"),
    input_type: yup.string().required().max(150).label("Input type"),
    input_inference_path: yup.string().required().max(250).label("Input inference"),
    output_inference_path: yup.string().required().max(250).label("Output inference"),
    instance_count: yup.number().required().max(4).label("Instance count"),
    max_payload_inmb: yup.number().required().max(5).label("Max payload in mb"),
    max_concurrent_transforms: yup.number().required().max(5).label("Max concurrent transforms"),
    instance_type: yup.string().required().max(50).label("Instance type"),
});

export const endpointCreationValidations = yup.object({
    ...templateCreationGeneralValidations,
    ...modelConfigurationValidation,
    ...githubConfigValidation,

    // Endpoint config
    variant_name: yup.string().max(150).required().label("Variant name"),
    instance_count: yup.number().max(10).min(1).required().label("Instance count"),
    instance_type: yup.string().max(50).required().label("Instance type"),
    enable_fastapi: yup.boolean().label("Enable FastAPI"),
});

export const trainingTemplateValidations = yup.object({
    ...templateCreationGeneralValidations,
    ...githubConfigValidation,
    ...schedulingValidation,

    // Computer resources
    instance_count: yup.number().max(10).min(1).required().label("Instance count"),
    instance_type: yup.string().required().max(50).label("Instance type"),
    volume_size: yup.number().required().max(256).min(16).label("Volume size"),

    // Model configuration
    max_retries: yup.number().max(10).min(1).label("Max retries"),
    train_comp_type: yup.string().max(30).required().label("Train comp type"),
    train_dist_type: yup.string().max(50).required().label("Train dist type"),
    train_s3_uri: yup.string().matches(s3URIRegex, "Must be a valid S3 URI").max(300).required().label("Train S3 URI"),
    validation_comp_type: yup.string().max(20).required().label("Validation comp type"),
    validation_dist_type: yup.string().max(50).required().label("Validation dist type"),
    validation_s3_uri: yup.string().matches(s3URIRegex, "Must be a valid S3 URI").max(300).required().label("Validation S3 URI"),
    test_comp_type: yup.string().max(30).required().label("Test comp type"),
    test_dist_type: yup.string().max(50).required().label("Test dist type"),
    test_s3_uri: yup.string().matches(s3URIRegex, "Must be a valid S3 URI").max(300).required().label("Test S3 URI"),
    
    // W&B config
    wandb_project_name: yup.string().required().max(150).label("Wandb project name"),
    model_name_custom: yup.string().required().max(150).label("Model name"),
    wandb_model_version: yup.string().required().max(50).label("Wandb model version"),
});

export const cloudServiceValidationSchema = yup.object({
  pod: yup.string().required("Pod Name is a required field"),
  description: yup.string().required("Description is a required field"),
  businessJustification: yup
    .string()
    .required("Business justification is a required field"),
});
